export const normalizeApiResponse = (response) => {
    if (response == null) {
        return { success: false, message: '', data: null, raw: response };
    }

    const hasSuccessField = typeof response.success === 'boolean';
    const hasNestedData = response.data !== undefined;
    const isDirectEntity =
        !hasSuccessField &&
        !hasNestedData &&
        typeof response === 'object' &&
        !Array.isArray(response);

    let data = null;
    if (hasNestedData) {
        data = response.data;
    } else if (isDirectEntity) {
        data = response;
    }

    const success = hasSuccessField
        ? response.success !== false
        : !response.error;

    return {
        success: success && !response.error,
        message: response.message || response.error || '',
        data,
        raw: response,
    };
};

export const isApiSuccess = (response) => normalizeApiResponse(response).success;

export const getApiData = (response) => {
    const { data, raw } = normalizeApiResponse(response);
    return data !== undefined && data !== null ? data : raw;
};

export const getApiEntity = (response) => {
    const data = getApiData(response);
    return data && typeof data === 'object' && !Array.isArray(data) ? data : response;
};

export const getApiMessage = (response, fallback = '') =>
    normalizeApiResponse(response).message || fallback;

export const extractApiList = (response) => {
    const data = getApiData(response);

    if (Array.isArray(data)) return data;
    if (Array.isArray(response)) return response;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.selfies)) return data.selfies;
    if (Array.isArray(data?.products)) return data.products;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.addresses)) return data.addresses;
    if (Array.isArray(data?.notifications)) return data.notifications;

    return [];
};

export const getApiPageMeta = (response) => {
    const data = getApiData(response);
    if (!data || Array.isArray(data)) return null;

    return {
        totalRecords: data.totalRecords ?? data.total_records ?? 0,
        totalPages: data.total_pages ?? data.totalPages ?? 1,
        page: data.page ?? 1,
        perPage: data.perPage ?? data.per_page ?? 10,
    };
};

export const toProductDisplayPrice = (amount) => {
    if (amount == null || Number.isNaN(Number(amount))) return 0;
    return Number(amount);
};

const getVariantId = (variant) => {
    if (!variant) return null;
    if (typeof variant === 'string') return variant;
    return variant._id || variant.id || null;
};

const SIZE_OPTION_NAMES = ['size', 'taille'];
const COLOR_OPTION_NAMES = ['color', 'couleur', 'colour'];

const matchesOptionName = (name, candidates) => {
    const normalized = String(name || '').toLowerCase().trim();
    return candidates.some(
        (candidate) => normalized === candidate || normalized.includes(candidate)
    );
};

const getVariantSize = (variant) => {
    if (variant?.size) return variant.size;

    const sizeOption = (variant?.option_ids || []).find((opt) =>
        matchesOptionName(opt?.name, SIZE_OPTION_NAMES)
    );

    if (sizeOption?.value) return sizeOption.value;

    if (variant?.option2) return variant.option2;

    return null;
};

const getVariantColor = (variant) => {
    if (variant?.color) return variant.color;

    const colorOption = (variant?.option_ids || []).find((opt) =>
        matchesOptionName(opt?.name, COLOR_OPTION_NAMES)
    );

    if (colorOption?.value) return colorOption.value;

    if (variant?.option1) return variant.option1;

    return null;
};

const getVariantOptionValue = (variant, optionName) => {
    if (optionName.toLowerCase() === 'color') {
        return getVariantColor(variant);
    }
    if (optionName.toLowerCase() === 'size') {
        return getVariantSize(variant);
    }

    const option = (variant?.option_ids || []).find(
        (opt) => String(opt?.name || '').toLowerCase() === optionName.toLowerCase()
    );

    return option?.value || null;
};

const COLOR_SWATCHES = {
    white: '#ffffff',
    black: '#111111',
    red: '#c0392b',
    blue: '#2980b9',
    green: '#27ae60',
    grey: '#95a5a6',
    gray: '#95a5a6',
    beige: '#d4c4a8',
    brown: '#6e4b2a',
    navy: '#1a2744',
    pink: '#f8b4c4',
    yellow: '#f1c40f',
    orange: '#e67e22',
    purple: '#8e44ad',
    noir: '#111111',
    'bleu marine': '#1a2744',
    'rouge bordeaux': '#722f37',
    bordeaux: '#722f37',
};

export const getColorSwatch = (colorName) => {
    if (!colorName) return '#cccccc';
    const key = String(colorName).toLowerCase().trim();
    return COLOR_SWATCHES[key] || '#cccccc';
};

export const findProductVariant = (variants = [], { color, size } = {}) => {
    const list = Array.isArray(variants) ? variants : [];

    if (color && size) {
        return list.find((v) => v.color === color && v.size === size) || null;
    }
    if (color) {
        return list.find((v) => v.color === color) || null;
    }
    if (size) {
        return list.find((v) => v.size === size) || null;
    }

    return list[0] || null;
};

export const getVariantById = (variants = [], variantId) => {
    if (!variantId) return null;
    return (Array.isArray(variants) ? variants : []).find(
        (v) => v._id === variantId || v.id === variantId
    ) || null;
};

export const resolveProductVariantId = (product, { color, size } = {}) => {
    if (!product) return null;

    const variant = findProductVariant(product.variants, { color, size });

    return (
        variant?._id ||
        variant?.id ||
        product.variant_lookup?.[`${color || ""}|${size || ""}`] ||
        product.variant_map?.[size] ||
        product.size_variants?.[size] ||
        product.variants?.[0]?._id ||
        product.variants?.[0]?.id ||
        null
    );
};

export const getSizesForColor = (product, color) => {
    const variants = product?.variants || [];
    const sizes = [];

    variants.forEach((variant) => {
        if (color && variant.color !== color) return;
        if (variant.size && !sizes.includes(variant.size)) {
            sizes.push(variant.size);
        }
    });

    return sizes;
};

const collectProductVariants = (entry, product = {}) => {
    const seen = new Set();
    const list = [];

    [
        ...(Array.isArray(entry?.variants) ? entry.variants : []),
        ...(Array.isArray(product?.variants) ? product.variants : []),
    ].forEach((variant) => {
        const id = getVariantId(variant);
        const key = id || JSON.stringify(variant);
        if (seen.has(key)) return;
        seen.add(key);
        list.push(variant);
    });

    return list;
};

export const normalizeVariantsList = (variants = []) =>
    variants.map((variant) => ({
        ...variant,
        size: getVariantSize(variant),
        color: getVariantColor(variant),
        price_eur: toProductDisplayPrice(variant.price),
        compare_at_price_eur: variant.compare_at_price
            ? toProductDisplayPrice(variant.compare_at_price)
            : null,
    }));

export const resolveLinkedVariant = (entry) => {
    const product = entry?.product_id && typeof entry.product_id === 'object' ? entry.product_id : {};
    const variants = collectProductVariants(entry, product);
    const rawVariant = entry?.variant_id;

    if (rawVariant && typeof rawVariant === 'object') {
        const id = getVariantId(rawVariant);
        const fromArray = id ? variants.find((v) => getVariantId(v) === id) : null;
        return { ...(fromArray || {}), ...rawVariant };
    }

    const variantId = getVariantId(rawVariant);
    if (variantId) {
        const match = variants.find((v) => getVariantId(v) === variantId);
        if (match) return match;
        return { _id: variantId };
    }

    if (Array.isArray(entry?.variants) && entry.variants[0]) {
        return entry.variants[0];
    }

    return variants[0] || {};
};

export const getSelfieProductPriceRaw = (itemData) => {
    if (!itemData) return 0;
    if (itemData.item?.price != null) return Number(itemData.item.price) || 0;

    const variant = resolveLinkedVariant(itemData);
    const variants = itemData.variants || [];
    const raw =
        variant?.price ??
        variants[0]?.price ??
        (typeof itemData.product_id === 'object' ? itemData.product_id.price : null) ??
        0;

    return Number(raw) || 0;
};

export const getSelfieProductPriceEur = (itemData) => {
    if (!itemData) return 0;
    if (itemData.item?.price_eur != null) return itemData.item.price_eur;

    return toProductDisplayPrice(getSelfieProductPriceRaw(itemData));
};

export const formatPriceEur = (amount) => {
    const value =
        typeof amount === 'object' && amount !== null
            ? getSelfieProductPriceRaw(amount)
            : amount;

    if (value == null || value === '') return '0€';
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return `${value}€`;

    return `${numeric}€`;
};

export const mapSelfieProductRef = (entry, exactMatch = true) => {
    if (!entry) return null;

    const product = entry.product_id || {};
    const brandRef = resolveBrandRef(entry, product);
    const productId =
        product._id ||
        product.id ||
        (typeof entry.product_id === 'string' ? entry.product_id : null);

    if (!productId) return null;

    const variant = resolveLinkedVariant(entry);
    const variants = normalizeVariantsList(collectProductVariants(entry, product));
    const pictures =
        variant.images ||
        variants[0]?.images ||
        product.pictures ||
        product.images ||
        (product.product_image ? [product.product_image] : []);

    const rawPrice = variant.price ?? variants[0]?.price ?? product.price ?? 0;
    const rawCompareAt = variant.compare_at_price ?? product.compare_at_price ?? null;

    return {
        id: productId,
        exact_match: exactMatch,
        item: {
            ...product,
            _id: productId,
            pictures,
            brand_id: brandRef,
            price: rawPrice,
            price_eur: toProductDisplayPrice(rawPrice),
            compare_at_price: rawCompareAt ? toProductDisplayPrice(rawCompareAt) : null,
            size: getVariantSize(variant),
            color: getVariantColor(variant),
        },
        brand_id: brandRef,
        product_id: product,
        variant_id: variant,
        variants,
    };
};

export const getBrandImageUrl = (brand) => {
    if (!brand) return null;

    if (typeof brand === 'string') return null;

    const url =
        brand.picture ||
        brand.logo ||
        brand.brand_picture ||
        brand.image ||
        brand.logo_url ||
        brand.picture_url ||
        null;

    return typeof url === 'string' ? url.trim() : url;
};

export const getBrandDescription = (brand) => {
    if (!brand || typeof brand !== 'object') return '';

    return (
        brand.description ||
        brand.bio ||
        brand.about ||
        brand.brand_description ||
        brand.short_description ||
        brand.tagline ||
        ''
    );
};

export const resolveBrandRef = (entry, product = entry?.product_id) => {
    const productObj = typeof product === 'object' ? product : {};
    const rawBrand = entry?.brand_id ?? entry?.brand;
    const nestedBrand =
        typeof productObj?.brand_id === 'object' ? productObj.brand_id : null;

    if (rawBrand && typeof rawBrand === 'object') {
        return {
            _id: rawBrand._id || rawBrand.id || null,
            name: rawBrand.name || rawBrand.brand_name || nestedBrand?.name || '',
            picture: getBrandImageUrl(rawBrand) || entry?.brand_picture || getBrandImageUrl(nestedBrand),
            description: getBrandDescription(rawBrand) || getBrandDescription(nestedBrand),
        };
    }

    if (typeof rawBrand === 'string') {
        return {
            _id: rawBrand,
            name: entry?.brand_name || nestedBrand?.name || '',
            picture: entry?.brand_picture || getBrandImageUrl(nestedBrand),
            description: getBrandDescription(nestedBrand) || entry?.brand_description || '',
        };
    }

    if (nestedBrand) {
        return {
            _id: nestedBrand._id || nestedBrand.id || null,
            name: nestedBrand.name || entry?.brand_name || '',
            picture: getBrandImageUrl(nestedBrand) || entry?.brand_picture || null,
            description: getBrandDescription(nestedBrand),
        };
    }

    return {
        _id: null,
        name: entry?.brand_name || '',
        picture: entry?.brand_picture || null,
        description: entry?.brand_description || '',
    };
};

export const buildBrandCache = (entries = []) => {
    const cache = {};

    entries.forEach((entry) => {
        const brand = resolveBrandRef(entry, entry?.product_id || entry);
        if (!brand?._id) return;

        cache[brand._id] = {
            name: brand.name || cache[brand._id]?.name || '',
            picture: brand.picture || cache[brand._id]?.picture || null,
            description: brand.description || cache[brand._id]?.description || '',
        };
    });

    return cache;
};

const BRAND_CACHE_STORAGE_KEY = 'happerBrandCache';

export const loadBrandCache = () => {
    try {
        return JSON.parse(sessionStorage.getItem(BRAND_CACHE_STORAGE_KEY) || '{}');
    } catch {
        return {};
    }
};

export const saveBrandCache = (cache) => {
    try {
        sessionStorage.setItem(BRAND_CACHE_STORAGE_KEY, JSON.stringify(cache || {}));
    } catch {
        // ignore quota errors
    }
};

export const mergeBrandCacheEntries = (prevCache = {}, entries = []) => ({
    ...prevCache,
    ...buildBrandCache(entries),
});

const buildSelfieItemsId = (selfie) => {
    const linked = (selfie.linked_products || [])
        .map((entry) => mapSelfieProductRef(entry, true))
        .filter(Boolean);
    const similar = (selfie.similar_products || [])
        .map((entry) => mapSelfieProductRef(entry, false))
        .filter(Boolean);

    if (linked.length || similar.length) {
        return [...linked, ...similar];
    }

    if (Array.isArray(selfie.items_id) && selfie.items_id.length) {
        return selfie.items_id
            .map((entry) => {
                if (entry?.item?.price != null || entry?.item?.price_eur != null) {
                    if (entry.item?.price_eur != null) return entry;

                    return {
                        ...entry,
                        item: {
                            ...entry.item,
                            price_eur: toProductDisplayPrice(entry.item.price),
                        },
                    };
                }

                return (
                    mapSelfieProductRef(
                        {
                            brand_id: entry.brand_id,
                            product_id: entry.product_id || entry.item,
                            variant_id: entry.variant_id,
                            variants:
                                entry.variants ||
                                entry.product_id?.variants ||
                                entry.item?.variants,
                        },
                        entry.exact_match !== false
                    ) || entry
                );
            })
            .filter(Boolean);
    }

    return [];
};

export const isUserVerified = (user) =>
    Boolean(user?.verified ?? user?.is_verified ?? user?.isVerified);

export const getCreatorDisplayName = (userOrSelfie) => {
    const user = userOrSelfie?.user || userOrSelfie || {};
    return (
        user.username ||
        user.first_name ||
        user.full_name ||
        [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
        ""
    );
};

export const normalizeSelfieItem = (selfie) => {
    if (!selfie || typeof selfie !== 'object') return selfie;

    const images = selfie.images || selfie.pictures || [];
    const picture = selfie.picture || images[0] || null;
    const userSource =
        selfie.user ||
        (selfie.user_id && typeof selfie.user_id === 'object' ? selfie.user_id : null) ||
        {};
    const userId =
        userSource._id ||
        (typeof selfie.user_id === 'string' ? selfie.user_id : null);
    const verified = Boolean(
        userSource.verified ?? userSource.is_verified ?? userSource.isVerified
    );

    return {
        ...selfie,
        picture,
        pictures: selfie.pictures || images,
        images,
        isLikedByMe: selfie.isLikedByMe ?? selfie.is_liked_by_me ?? false,
        nb_like: selfie.nb_like ?? selfie.likes_count ?? selfie.likes?.length ?? 0,
        affiliate_id: userId,
        user: {
            ...userSource,
            _id: userId,
            picture: userSource.picture || userSource.profile_image || null,
            verified,
        },
        items_id: buildSelfieItemsId(selfie),
        linked_brands: selfie.linked_brands || [],
        linked_products: selfie.linked_products || [],
        similar_products: selfie.similar_products || [],
        recommended_products: selfie.recommended_products || [],
        caption: selfie.caption || '',
    };
};

export const getSelfieAffiliateId = (selfie) => {
    if (!selfie) return null;

    return (
        selfie.affiliate_id ||
        selfie.user?._id ||
        (selfie.user_id && typeof selfie.user_id === 'object' ? selfie.user_id._id : null) ||
        (typeof selfie.user_id === 'string' ? selfie.user_id : null)
    );
};

export const normalizeSelfieList = (list) =>
    Array.isArray(list) ? list.map(normalizeSelfieItem) : [];

const normalizeSuggestionItem = (entry, fallbackType) => {
    if (!entry || typeof entry !== 'object') return null;

    const rawType = entry.type || fallbackType;
    let type = rawType === 'user' ? 'creator' : rawType;

    const brandName = entry.brand_name || entry.brandName || '';
    const name =
        entry.name ||
        brandName ||
        entry.username ||
        entry.full_name ||
        `${entry.first_name || ''} ${entry.last_name || ''}`.trim();

    if (!type) {
        type = entry.username || entry.full_name ? 'creator' : 'brand';
    }

    if (type === 'creator' && !entry.username && !entry.full_name && brandName) {
        type = 'brand';
    }

    const id = entry._id || entry.id;
    if (!id && !name) return null;

    return {
        type,
        _id: id,
        name,
        username: entry.username || '',
        full_name:
            entry.full_name ||
            `${entry.first_name || ''} ${entry.last_name || ''}`.trim() ||
            name,
        picture:
            entry.picture ||
            entry.profile_image ||
            entry.logo ||
            null,
    };
};

export const normalizeSelfieSuggestions = (response) => {
    const list = extractApiList(response);

    if (list.length > 0) {
        return list
            .map((entry) => normalizeSuggestionItem(entry))
            .filter(Boolean);
    }

    const data = getApiData(response);
    const entity = getApiEntity(response);
    const source = data && typeof data === 'object' ? data : entity;

    if (Array.isArray(source)) {
        return source.map((entry) => normalizeSuggestionItem(entry)).filter(Boolean);
    }

    const creators = (source?.creators || source?.users || []).map((entry) =>
        normalizeSuggestionItem(entry, 'creator')
    );
    const brands = (source?.brands || []).map((entry) =>
        normalizeSuggestionItem(entry, 'brand')
    );

    return [...creators, ...brands].filter(Boolean);
};

export const normalizeProductItem = (product) => {
    if (!product || typeof product !== 'object') return product;

    const brandRef = resolveBrandRef(product, product);

    const variants = normalizeVariantsList(product.variants || []);

    const size_of_product = {};
    const variant_map = {};
    const size_variants = {};
    const price_by_size = {};
    const variant_lookup = {};
    const sizes_by_color = {};
    const colorsMap = new Map();

    variants.forEach((variant) => {
        const colorKey = variant.color || '_default';
        const lookupKey = `${variant.color || ''}|${variant.size || ''}`;

        if (lookupKey !== '|') {
            variant_lookup[lookupKey] = variant._id;
        }

        if (variant.color) {
            if (!colorsMap.has(variant.color)) {
                colorsMap.set(variant.color, {
                    name: variant.color,
                    swatch: getColorSwatch(variant.color),
                    image: variant.images?.[0] || null,
                    available: (variant.quantity ?? 0) > 0,
                });
            } else {
                const entry = colorsMap.get(variant.color);
                entry.available = entry.available || (variant.quantity ?? 0) > 0;
            }
        }

        if (variant.size) {
            if (!sizes_by_color[colorKey]) {
                sizes_by_color[colorKey] = {};
            }
            sizes_by_color[colorKey][variant.size] = variant.quantity ?? 0;

            if (!variant.color) {
                size_of_product[variant.size] = variant.quantity ?? 0;
                variant_map[variant.size] = variant._id;
                size_variants[variant.size] = variant._id;
                price_by_size[variant.size] = variant.price ?? 0;
            }
        }
    });

    const colors = Array.from(colorsMap.values());

    const pictures = [
        ...new Set(
            [
                ...(product.pictures || []),
                ...(product.images || []),
                product.product_image,
                ...variants.flatMap((variant) => variant.images || []),
            ].filter(Boolean)
        ),
    ];

    const variantPrices = variants
        .map((variant) => variant.price)
        .filter((price) => price != null);
    const price = product.price ?? variants[0]?.price ?? 0;
    const minPrice = variantPrices.length ? Math.min(...variantPrices) : Number(price) || 0;
    const minPriceVariant =
        variants.find((variant) => Number(variant.price) === minPrice) || variants[0];
    const variantCompareAt = minPriceVariant?.compare_at_price;
    const productCompareAt =
        product.compare_at_price ??
        (variantCompareAt != null && Number(variantCompareAt) > minPrice
            ? variantCompareAt
            : null);

    return {
        ...product,
        pictures,
        price: minPrice,
        compare_at_price: productCompareAt,
        price_by_size,
        variants,
        colors,
        sizes_by_color,
        variant_lookup,
        size_of_product,
        variant_map,
        size_variants,
        brand_id: brandRef,
        isLikedByMe: product.isLikedByMe ?? product.is_liked_by_me ?? false,
    };
};

export const normalizeProductLikeResponse = (response, action) => {
    const entity = getApiEntity(response);

    let isLikedByMe =
        entity?.isLikedByMe ??
        entity?.is_liked_by_me ??
        entity?.is_liked;

    if (isApiSuccess(response) && action) {
        isLikedByMe = action === 'like';
    }

    return {
        success: isApiSuccess(response),
        message: getApiMessage(response),
        product_id: entity?.product_id ?? entity?._id,
        isLikedByMe,
        data: entity || null,
        raw: response,
    };
};

export const normalizeLikedProductItem = (entry) => {
    if (!entry || typeof entry !== 'object') return null;

    const product = typeof entry.product_id === 'object' ? entry.product_id : {};
    const productId =
        product._id ||
        product.id ||
        (typeof entry.product_id === 'string' ? entry.product_id : null);

    if (!productId) return null;

    const variant = resolveLinkedVariant(entry);
    const variantId = variant?._id || variant?.id || null;
    const brandRef = resolveBrandRef(entry, product);
    const images = [
        ...(Array.isArray(variant?.images) ? variant.images : []),
        product.product_image,
        ...(Array.isArray(product.pictures) ? product.pictures : []),
        ...(Array.isArray(product.images) ? product.images : []),
    ].filter(Boolean);
    const pictures = [...new Set(images)];

    return {
        _id: productId,
        likeId: entry._id,
        variantId,
        name: product.name || '',
        pictures,
        product_image: product.product_image || pictures[0] || null,
        brand_id: brandRef,
        price: variant?.price ?? product.price ?? 0,
        compare_at_price: variant?.compare_at_price ?? product.compare_at_price ?? null,
        isLikedByMe: true,
    };
};

export const normalizeLikedProductList = (list) =>
    Array.isArray(list) ? list.map(normalizeLikedProductItem).filter(Boolean) : [];

export const normalizeSelfieLikeResponse = (response, action) => {
    const entity = getApiEntity(response);
    const normalized =
        entity && typeof entity === 'object' && !Array.isArray(entity)
            ? normalizeSelfieItem(entity)
            : null;

    const likesCount = entity?.likes_count ?? entity?.nb_like ?? normalized?.nb_like;

    let isLikedByMe =
        normalized?.isLikedByMe ??
        entity?.is_liked_by_me ??
        entity?.isLikedByMe;

    if (isApiSuccess(response) && action) {
        isLikedByMe = action === 'like';
    }

    return {
        success: isApiSuccess(response),
        message: getApiMessage(response),
        selfie_id: entity?.selfie_id ?? entity?._id ?? normalized?._id,
        isLikedByMe,
        nb_like: likesCount,
        likes_count: likesCount,
        data: normalized || entity || null,
        raw: response,
    };
};

export const normalizeCartAddress = (address) => {
    if (!address || typeof address !== 'object') return {};

    return {
        ...address,
        name:
            address.name ||
            `${address.first_name || ''} ${address.last_name || ''}`.trim() ||
            address.address_label ||
            '',
        phone: address.phone || address.mobile_number || '',
        address: address.address || address.street_address || '',
        city: address.city || '',
        country: address.country || '',
        zip: address.zip || address.postal_code || '',
        state: address.state || '',
        email: address.email || '',
    };
};

export const normalizeCartItem = (item) => {
    if (!item || typeof item !== 'object') return item;

    const product =
        item.product ||
        (typeof item.product_id === 'object' ? item.product_id : { _id: item.product_id }) ||
        {};
    const rawVariant = item.variant ?? item.variant_id;
    const variantId =
        typeof rawVariant === 'string'
            ? rawVariant
            : rawVariant?._id || rawVariant?.id || null;
    const productVariants = normalizeVariantsList(product.variants || []);
    const resolvedVariant =
        (variantId ? getVariantById(productVariants, variantId) : null) ||
        (rawVariant && typeof rawVariant === 'object'
            ? normalizeVariantsList([rawVariant])[0]
            : null) ||
        {};
    const affiliate =
        typeof item.affiliate_id === 'object' ? item.affiliate_id : { _id: item.affiliate_id };
    const brand =
        typeof item.brand_id === 'object' ? item.brand_id : { _id: item.brand_id };

    const size =
        item.size ||
        resolvedVariant.size ||
        getVariantSize(rawVariant && typeof rawVariant === 'object' ? rawVariant : {}) ||
        '';
    const color =
        item.color ||
        resolvedVariant.color ||
        getVariantColor(rawVariant && typeof rawVariant === 'object' ? rawVariant : {}) ||
        '';

    const variantImage = resolvedVariant.images?.[0] || null;
    const basePictures = [
        ...(product.pictures || []),
        ...(product.images || []),
        product.product_image,
        ...(item.images || []),
        ...(resolvedVariant.images || []),
    ].filter(Boolean);
    const uniquePictures = variantImage
        ? [variantImage, ...basePictures.filter((pic) => pic !== variantImage)]
        : [...new Set(basePictures)];

    const unitPrice =
        item.unit_price ?? item.price ?? resolvedVariant.price ?? product.price;
    const priceEur = toProductDisplayPrice(unitPrice);
    const compareAt =
        item.compare_at_price ??
        resolvedVariant.compare_at_price ??
        product.compare_at_price ??
        null;
    const compareAtEur =
        compareAt != null && Number(compareAt) > Number(unitPrice)
            ? toProductDisplayPrice(compareAt)
            : null;

    return {
        ...item,
        _id: item._id || item.id || item.cart_item_id,
        name: item.name || product.name,
        price: priceEur,
        compare_at_price: compareAtEur,
        unit_price_eur: priceEur,
        total_eur: toProductDisplayPrice(item.total ?? unitPrice),
        size,
        color,
        quantity: item.quantity || 1,
        affiliate_id: affiliate,
        brand_id: brand,
        product_id: product,
        variant_id: resolvedVariant._id || resolvedVariant.id || variantId || rawVariant,
        item_id: {
            ...product,
            pictures: uniquePictures.length ? uniquePictures : [],
        },
    };
};

export const normalizeCartData = (cart) => {
    if (!cart || typeof cart !== 'object') {
        return { items: [], total: 0, total_price_without_shipping: 0, total_shipping_price: 0 };
    }

    const rawItems = cart.items || cart.cart_items || cart.products || [];
    const items = rawItems.map(normalizeCartItem);
    const linkedRaw = typeof cart.shipping_address_id === 'object' ? cart.shipping_address_id : null;
    const linked = normalizeCartAddress(linkedRaw);
    const inline = normalizeCartAddress(cart.shipping_address);
    const shippingAddress = {
        ...linked,
        ...inline,
        email: inline.email || linked.email || '',
        phone: inline.phone || linked.phone || '',
        name: inline.name || linked.name || '',
        address: inline.address || linked.address || '',
        city: inline.city || linked.city || '',
        zip: inline.zip || linked.zip || '',
        country: inline.country || linked.country || '',
        state: inline.state || linked.state || '',
    };

    return {
        ...cart,
        items,
        item_count: cart.item_count ?? items.length,
        total: toProductDisplayPrice(cart.total ?? cart.grand_total ?? 0),
        total_price_without_shipping: toProductDisplayPrice(
            cart.subtotal ?? cart.total_price_without_shipping ?? 0
        ),
        total_shipping_price: toProductDisplayPrice(
            cart.shipping_amount ?? cart.shipping ?? cart.total_shipping_price ?? 0
        ),
        shipping_address: shippingAddress,
        shipping_address_id:
            linkedRaw?._id || cart.shipping_address_id?._id || cart.shipping_address_id || null,
        user_id: cart.user_id || cart.user,
        currency: cart.currency || 'EUR',
    };
};

export const normalizeCartResponse = (raw) => {
    const cart = getApiEntity(raw) || raw?.cart || {};

    return {
        success: isApiSuccess(raw),
        message: getApiMessage(raw),
        data: normalizeCartData(cart),
    };
};

export const normalizePurchasedProductItem = (item) => {
    if (!item || typeof item !== 'object') return item;

    const product =
        (typeof item.product_id === 'object' ? item.product_id : null) ||
        item.product ||
        {};
    const variant =
        (typeof item.variant_id === 'object' ? item.variant_id : null) ||
        item.variant ||
        {};

    const pictures = [
        ...(variant.images || []),
        product.product_image,
        ...(product.pictures || []),
        ...(product.images || []),
    ]
        .filter(Boolean)
        .map((url) => String(url).trim());

    const uniquePictures = [...new Set(pictures)];
    const unitPrice = item.unit_price ?? item.price ?? variant.price;
    const priceEur = toProductDisplayPrice(unitPrice);
    const compareAt =
        item.compare_at_price ?? variant.compare_at_price ?? product.compare_at_price ?? null;
    const comparePrice =
        compareAt != null && Number(compareAt) > Number(unitPrice)
            ? toProductDisplayPrice(compareAt)
            : null;

    return {
        ...item,
        _id: item._id || item.cart_item_id || item.order_id,
        name: item.name || product.name,
        price: priceEur,
        compare_at_price: comparePrice,
        status: item.status || product.status || 'paid',
        invoice_link: item.invoice_link || null,
        delivery_link: item.delivery_link || null,
        paid_at: item.paid_at,
        shipped_at: item.shipped_at || item.sent_at || null,
        delivered_at: item.delivered_at || item.deliveredAt || null,
        tracking_number: item.tracking_number || item.tracking || null,
        carrier: item.carrier || item.delivery_carrier || null,
        size: item.size || variant.size || "",
        order_id: item.order_id,
        cart_item_id: item.cart_item_id,
        brand_id: typeof item.brand_id === 'object' ? item.brand_id : { _id: item.brand_id },
        affiliate_id:
            typeof item.affiliate_id === 'object' ? item.affiliate_id : { _id: item.affiliate_id },
        shipping_address: item.shipping_address || item.delivery_address || item.address || null,
        selfie_id: item.selfie_id || item.look_id || item.image_id || null,
        quantity: item.quantity || 1,
        item_id: {
            ...product,
            pictures: uniquePictures,
        },
    };
};

export const normalizePurchasedProductList = (list) =>
    Array.isArray(list) ? list.map(normalizePurchasedProductItem) : [];

export const getPaymentClientSecret = (response) => {
    const data = getApiEntity(response) || getApiData(response) || {};

    if (typeof data === 'string' && data.includes('_secret_')) {
        return data;
    }

    return (
        data.client_secret ||
        data.clientSecret ||
        data.payment_intent?.client_secret ||
        data.paymentIntent?.client_secret ||
        null
    );
};

export const normalizeUserProfile = (response) => {
    const user = getApiEntity(response) || getApiData(response);
    if (!user || typeof user !== 'object' || Array.isArray(user)) return null;

    return {
        ...user,
        picture: user.picture || user.profile_image || null,
        full_name:
            user.full_name ||
            `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
            user.username ||
            '',
    };
};

export const normalizeUserProfileStats = (user) => {
    const profile = normalizeUserProfile(user);
    if (!profile) return null;

    const followersCount =
        profile.followers_count ?? profile.followers ?? profile.follower_count ?? 0;

    return {
        ...profile,
        followers_count: followersCount,
        following_count: profile.following_count ?? profile.following ?? 0,
        selfies_count: profile.selfies_count ?? profile.selfies ?? 0,
        is_following: profile.is_following ?? profile.isFollowedByMe ?? false,
        isFollowedByMe: profile.is_following ?? profile.isFollowedByMe ?? false,
        is_own_account: profile.is_own_account ?? false,
        follows: followersCount,
        instagram_link: profile.instagram_link || null,
        bio: profile.bio || '',
    };
};

export const getUploadedFileUrl = (response) => {
    const data = getApiData(response);
    const entity = getApiEntity(response);

    return (
        entity?.url ||
        entity?.file_url ||
        entity?.fileUrl ||
        data?.url ||
        data?.file_url ||
        response?.url ||
        response?.file_url ||
        response?.profileImage ||
        null
    );
};

export const extractBrandsFromItems = (items = []) => {
    const brandsMap = new Map();

    (Array.isArray(items) ? items : []).forEach((item) => {
        const brand = item?.brand_id || item?.item?.brand_id;
        const brandId = typeof brand === 'string' ? brand : brand?._id || brand?.id;
        if (!brandId) return;

        if (!brandsMap.has(brandId)) {
            brandsMap.set(brandId, {
                _id: brandId,
                name: brand?.name || brand?.brand_name || '',
                picture: getBrandImageUrl(brand),
                description: getBrandDescription(brand),
            });
        }
    });

    return Array.from(brandsMap.values());
};

export const getProductImage = (product) =>
    product?.pictures?.[0] ||
    product?.product_image ||
    product?.variants?.[0]?.images?.[0] ||
    null;

export const getProductDisplayPrice = (product) => {
    if (!product) return 0;
    if (product.price != null) return Number(product.price) || 0;

    const prices = (product.variants || [])
        .map((variant) => variant.price)
        .filter((price) => price != null);

    return prices.length ? Math.min(...prices) : 0;
};

const resolveProductVariantForPrice = (product, variant = null) => {
    if (variant) return variant;

    const variants = product?.variants || [];
    if (!variants.length) return null;

    const productPrice = product?.price;
    if (productPrice != null) {
        const match = variants.find((entry) => Number(entry.price) === Number(productPrice));
        if (match) return match;
    }

    return variants.reduce((best, entry) => {
        if (!best) return entry;
        return Number(entry.price) < Number(best.price) ? entry : best;
    }, null);
};

export const getProductPriceDisplay = (product, variant = null) => {
    const resolvedVariant = resolveProductVariantForPrice(product, variant);
    const salePrice =
        Number(
            resolvedVariant?.price ??
                (product?.price != null ? product.price : null) ??
                product?.variants?.[0]?.price ??
                0
        ) || 0;

    let originalPrice = null;

    const variantCompare = resolvedVariant?.compare_at_price;
    if (variantCompare != null && variantCompare !== '') {
        const compareNum = Number(variantCompare);
        if (compareNum > salePrice) {
            originalPrice = compareNum;
        }
    }

    if (originalPrice == null && product?.compare_at_price != null && product.compare_at_price !== '') {
        const productCompare = Number(product.compare_at_price);
        if (productCompare > salePrice) {
            originalPrice = productCompare;
        }
    }

    const hasDiscount = originalPrice != null && originalPrice > salePrice;
    const discountPercent = hasDiscount
        ? Math.round((1 - salePrice / originalPrice) * 100)
        : 0;

    return {
        salePrice,
        originalPrice: hasDiscount ? originalPrice : null,
        discountPercent,
        hasDiscount,
    };
};

export const scalePriceInfo = (priceInfo, quantity = 1) => {
    const qty = Math.max(1, Number(quantity) || 1);
    if (!priceInfo?.salePrice) {
        return { salePrice: 0, originalPrice: null, discountPercent: 0, hasDiscount: false };
    }

    if (!priceInfo.hasDiscount) {
        return { ...priceInfo, salePrice: priceInfo.salePrice * qty };
    }

    return {
        salePrice: priceInfo.salePrice * qty,
        originalPrice: priceInfo.originalPrice * qty,
        discountPercent: priceInfo.discountPercent,
        hasDiscount: true,
    };
};

export const getSelfieItemPriceDisplay = (itemData) => {
    const variant = resolveLinkedVariant(itemData);
    const product =
        itemData?.item ||
        (typeof itemData?.product_id === 'object' ? itemData.product_id : null);

    return getProductPriceDisplay(product || itemData, variant);
};

export const getCartItemPriceDisplay = (item, { lineTotal = false } = {}) => {
    const product = item?.product_id || item?.item_id || {};
    const rawVariant = item?.variant_id;
    const variant =
        (rawVariant && typeof rawVariant === 'object' ? rawVariant : null) ||
        getVariantById(product.variants || [], rawVariant);

    let priceInfo = getProductPriceDisplay(product, variant);

    const unitPrice = item?.price ?? item?.unit_price ?? item?.unit_price_eur;
    if (unitPrice != null) {
        const salePrice = Number(unitPrice) || 0;
        let originalPrice = null;

        const compareAt =
            item.compare_at_price ?? variant?.compare_at_price ?? product.compare_at_price;
        if (compareAt != null && compareAt !== '') {
            const compareNum = Number(compareAt);
            if (compareNum > salePrice) {
                originalPrice = compareNum;
            }
        }

        const hasDiscount = originalPrice != null && originalPrice > salePrice;
        priceInfo = {
            salePrice,
            originalPrice: hasDiscount ? originalPrice : null,
            discountPercent: hasDiscount
                ? Math.round((1 - salePrice / originalPrice) * 100)
                : 0,
            hasDiscount,
        };
    }

    return lineTotal ? scalePriceInfo(priceInfo, item?.quantity || 1) : priceInfo;
};

export const getPurchasedItemPriceDisplay = (item) => {
    const product = item?.item_id || {};
    const variant =
        (typeof item?.variant_id === 'object' ? item.variant_id : null) ||
        getVariantById(product.variants || [], item?.variant_id);

    let priceInfo = getProductPriceDisplay(product, variant);

    if (item?.price != null) {
        const salePrice = Number(item.price) || 0;
        let originalPrice = item.compare_at_price ? Number(item.compare_at_price) : null;

        if (originalPrice != null && originalPrice <= salePrice) {
            originalPrice = null;
        }

        if (originalPrice == null && priceInfo.hasDiscount) {
            originalPrice = priceInfo.originalPrice;
        }

        const hasDiscount = originalPrice != null && originalPrice > salePrice;
        priceInfo = {
            salePrice,
            originalPrice: hasDiscount ? originalPrice : null,
            discountPercent: hasDiscount
                ? Math.round((1 - salePrice / originalPrice) * 100)
                : 0,
            hasDiscount,
        };
    }

    return priceInfo;
};

export const getDefaultProductVariantId = (product) => {
    if (!product) return null;

    const firstVariant = product.variants?.[0];
    if (firstVariant?._id) return firstVariant._id;

    const firstSize = Object.keys(product.variant_map || product.size_variants || {})[0];
    return (
        product.variant_map?.[firstSize] ||
        product.size_variants?.[firstSize] ||
        null
    );
};

export const productNeedsVariantSelection = (product) => {
    const colorCount = product?.colors?.length || 0;
    const sizeCount = Object.keys(product?.size_of_product || {}).length;

    if (colorCount > 1) return true;
    if (sizeCount > 1) return true;

    return (product?.variants?.length || 0) > 1;
};

export const selfieItemNeedsVariantSelection = (itemData) => {
    const product =
        itemData?.item ||
        (typeof itemData?.product_id === "object" ? itemData.product_id : null);

    if (!product) return false;

    const variants = itemData?.variants || product.variants || [];
    const uniqueColors = new Set(
        variants.map((variant) => getVariantColor(variant)).filter(Boolean)
    );
    const uniqueSizes = new Set(
        variants.map((variant) => getVariantSize(variant)).filter(Boolean)
    );

    if (uniqueColors.size > 1) return true;
    if (uniqueSizes.size > 1) return true;

    const colorCount = product.colors?.length || 0;
    const sizeCount = Object.keys(product.size_of_product || {}).length;

    if (colorCount > 1) return true;
    if (sizeCount > 1) return true;

    return variants.length > 1;
};

export const findCartLineForProduct = (cartItems = [], productId, variantId) =>
    (Array.isArray(cartItems) ? cartItems : []).find((item) => {
        const lineProductId = item?.product_id?._id || item?.product_id;
        const lineVariantId = item?.variant_id?._id || item?.variant_id;
        return lineProductId === productId && lineVariantId === variantId;
    }) || null;
